import { useState } from 'react';
import { Box, Typography, Chip, Stack, Avatar, Button, Menu, MenuItem, Tooltip, useTheme } from '@mui/material';
import LockOutlinedIcon from '@mui/icons-material/LockOutlined';
import EditOutlinedIcon from '@mui/icons-material/EditOutlined';
import WarningAmberRoundedIcon from '@mui/icons-material/WarningAmberRounded';
import HistoryRoundedIcon from '@mui/icons-material/HistoryRounded';
import HistoryEduOutlinedIcon from '@mui/icons-material/HistoryEduOutlined';
import AssignmentIndOutlinedIcon from '@mui/icons-material/AssignmentIndOutlined';
import ArrowDropDownRoundedIcon from '@mui/icons-material/ArrowDropDownRounded';
import SaveIndicator from './SaveIndicator';
import PriorEncountersDrawer from './PriorEncountersDrawer';

const NAVY = '#052F5D';

// Assignment shown in the encounter header. While a draft it's the assigned
// physician (editable for writers); once finalized it's the signer of record.
// Names resolve from the physician roster passed down from the editor.
function AssignmentBadge({ encounter, physicians, canAssign, onAssign }) {
    const [anchor, setAnchor] = useState(null);
    const isFinalized = encounter?.status && encounter.status !== 'draft';
    const byId = (id) => physicians.find((p) => String(p._id) === String(id || ''));

    let label;
    let unassigned = false;
    if (isFinalized) {
        const signer = byId(encounter?.reviewedBy);
        label = signer ? `Signed by ${signer.name}` : 'Finalized';
    } else {
        const dr = byId(encounter?.assignedPhysicianId);
        label = dr ? dr.name : 'Unassigned';
        unassigned = !dr;
    }

    const baseSx = unassigned ? {
        bgcolor: '#FFF6E5', color: '#5C3A00', border: '1px solid #F5C26B', fontWeight: 600,
        '.MuiChip-icon': { color: '#A65300' },
    } : {
        bgcolor: 'rgba(5,47,93,0.08)', color: NAVY, border: '1px solid rgba(5,47,93,0.15)', fontWeight: 600,
        '.MuiChip-icon': { color: NAVY },
    };

    if (!canAssign) {
        return (
            <Tooltip title={isFinalized ? 'Signing physician' : 'Assigned physician'} arrow>
                <Chip size="small" icon={<AssignmentIndOutlinedIcon sx={{ fontSize: 15 }} />} label={label} sx={baseSx} />
            </Tooltip>
        );
    }

    const current = String(encounter?.assignedPhysicianId || '');
    return (
        <>
            <Tooltip title="Change assigned physician" arrow>
                <Chip
                    size="small"
                    clickable
                    onClick={(e) => setAnchor(e.currentTarget)}
                    icon={<AssignmentIndOutlinedIcon sx={{ fontSize: 15 }} />}
                    deleteIcon={<ArrowDropDownRoundedIcon />}
                    onDelete={(e) => setAnchor(e.currentTarget)}
                    label={label}
                    sx={baseSx}
                />
            </Tooltip>
            <Menu anchorEl={anchor} open={Boolean(anchor)} onClose={() => setAnchor(null)}>
                <MenuItem
                    selected={!current}
                    onClick={() => { onAssign(null); setAnchor(null); }}
                >
                    <em>Unassigned</em>
                </MenuItem>
                {physicians.map((p) => (
                    <MenuItem
                        key={p._id}
                        selected={String(p._id) === current}
                        onClick={() => { onAssign(p._id); setAnchor(null); }}
                    >
                        {p.name}
                    </MenuItem>
                ))}
            </Menu>
        </>
    );
}

function ageFromDob(dob) {
    if (!dob) return null;
    const d = new Date(dob);
    if (Number.isNaN(d.getTime())) return null;
    const now = new Date();
    let age = now.getFullYear() - d.getFullYear();
    const m = now.getMonth() - d.getMonth();
    if (m < 0 || (m === 0 && now.getDate() < d.getDate())) age -= 1;
    return age;
}

function initialsOf(patient) {
    if (!patient) return '·';
    const a = (patient.fName || '').trim()[0];
    const b = (patient.lName || '').trim()[0];
    return `${a || ''}${b || ''}`.toUpperCase() || '·';
}

// Affirmative "no allergies / unknown" assertions are recorded like an
// allergy value but must NOT raise the orange alert strip — they mean the
// opposite. Keep this list in sync with AllergiesTab's NEGATIVE_RE.
const NEGATIVE_ALLERGY = new Set([
    'none', 'nkda', 'nka', 'no allergies', 'no known allergies', 'unknown', 'n/a',
]);

function readAllergies(patient) {
    let raw = patient?.allergies ?? patient?.medicalHistory?.allergies ?? [];
    // Allergies are stored as a comma-separated string on the patient record;
    // accept either a string or an array here.
    if (typeof raw === 'string') raw = raw.split(',');
    return raw
        .map((a) => (typeof a === 'string' ? a : a?.name))
        .filter((s) => s && !NEGATIVE_ALLERGY.has(String(s).trim().toLowerCase()));
}

// Lifecycle chip. Three states, because "finalized" and "finalized but
// currently being corrected" mean very different things to whoever picks
// the chart up next.
function statusChipProps(isFinalized, isAmending) {
    if (isAmending) {
        return {
            icon: <HistoryEduOutlinedIcon sx={{ fontSize: 14 }} />,
            label: 'Amending',
            bg: '#FFF6E5',
            fg: '#5C3A00',
            border: '#F5C26B',
        };
    }
    if (isFinalized) {
        return {
            icon: <LockOutlinedIcon sx={{ fontSize: 14 }} />,
            label: 'Finalized',
            bg: 'rgba(46,125,50,0.10)',
            fg: 'success.dark',
            border: 'rgba(46,125,50,0.25)',
        };
    }
    return {
        icon: <EditOutlinedIcon sx={{ fontSize: 14 }} />,
        label: 'Draft',
        bg: 'rgba(5,47,93,0.08)',
        fg: NAVY,
        border: 'rgba(5,47,93,0.15)',
    };
}

export default function PatientBanner({ patient, encounter, saveState, isFinalized, isAmending = false, physicians = [], canAssign = false, onAssign }) {
    const theme = useTheme();
    const age = ageFromDob(patient?.dob);
    const allergies = readAllergies(patient);
    const [historyOpen, setHistoryOpen] = useState(false);

    return (
        <Box sx={{ mb: 2 }}>
            {/* Main banner — patient identity + state */}
            <Box
                sx={{
                    bgcolor: '#fff',
                    borderRadius: 2,
                    border: `1px solid ${theme.palette.divider}`,
                    px: { xs: 2, md: 3 },
                    py: 1.75,
                    display: 'flex',
                    alignItems: 'center',
                    gap: { xs: 1.5, md: 2.5 },
                    flexWrap: 'wrap',
                    boxShadow: '0 1px 2px rgba(5,47,93,0.04)',
                }}
            >
                <Avatar
                    sx={{
                        bgcolor: NAVY,
                        color: '#fff',
                        width: 44,
                        height: 44,
                        fontSize: 16,
                        fontWeight: 600,
                        letterSpacing: 0.5,
                    }}
                >
                    {initialsOf(patient)}
                </Avatar>

                <Box sx={{ flex: 1, minWidth: 220 }}>
                    <Typography sx={{ fontWeight: 600, fontSize: '1.0625rem', lineHeight: 1.25, color: NAVY }}>
                        {patient ? `${patient.fName || ''} ${patient.lName || ''}`.trim() || 'Unknown patient' : 'Loading…'}
                    </Typography>
                    <Typography variant="caption" sx={{ color: 'text.secondary', display: 'block', mt: 0.25 }}>
                        MRN {patient?.mrn || '—'}
                        <Box component="span" sx={{ mx: 0.75, color: 'text.disabled' }}>·</Box>
                        {patient?.gender ? `${patient.gender[0].toUpperCase()}${patient.gender.slice(1)}` : '—'}
                        <Box component="span" sx={{ mx: 0.75, color: 'text.disabled' }}>·</Box>
                        {age !== null ? `${age} yrs` : '— yrs'}
                    </Typography>
                </Box>

                <Stack direction="row" spacing={1} alignItems="center" flexWrap="wrap" useFlexGap>
                    {patient?._id && (
                        <Button
                            size="small"
                            startIcon={<HistoryRoundedIcon sx={{ fontSize: 16 }} />}
                            onClick={() => setHistoryOpen(true)}
                            sx={{ textTransform: 'none', fontWeight: 600, color: NAVY }}
                        >
                            Prior encounters
                        </Button>
                    )}
                    <AssignmentBadge
                        encounter={encounter}
                        physicians={physicians}
                        canAssign={canAssign}
                        onAssign={onAssign}
                    />
                    {(() => {
                        const s = statusChipProps(isFinalized, isAmending);
                        return (
                            <Chip
                                size="small"
                                icon={s.icon}
                                label={s.label}
                                sx={{
                                    bgcolor: s.bg,
                                    color: s.fg,
                                    border: '1px solid',
                                    borderColor: s.border,
                                    fontWeight: 600,
                                    '.MuiChip-icon': { color: 'inherit' },
                                }}
                            />
                        );
                    })()}
                    {saveState && (
                        <SaveIndicator
                            status={saveState.status}
                            lastSavedAt={saveState.lastSavedAt}
                            error={saveState.error}
                            onRetry={saveState.onRetry}
                        />
                    )}
                </Stack>
            </Box>

            <PriorEncountersDrawer
                open={historyOpen}
                onClose={() => setHistoryOpen(false)}
                patientId={patient?._id}
                currentEncounterId={encounter?._id}
            />

            {/* Allergy strip — appears only when there ARE allergies. The
                allergy text gets flex:1 + minWidth:0 so a long list wraps
                inside the strip instead of pushing the icon off-screen on
                phones; desktop is unaffected (wraps only when needed). */}
            {allergies.length > 0 && (
                <Box
                    role="alert"
                    sx={{
                        mt: 1,
                        bgcolor: '#FFF6E5',
                        border: '1px solid #F5C26B',
                        borderLeft: '4px solid #C46A00',
                        borderRadius: 1.5,
                        px: 2,
                        py: 1,
                        display: 'flex',
                        alignItems: 'flex-start',
                        gap: 1.5,
                    }}
                >
                    <WarningAmberRoundedIcon sx={{ color: '#A65300', fontSize: 20, mt: '2px', flexShrink: 0 }} />
                    <Typography variant="body2" sx={{ color: '#5C3A00', fontWeight: 600, flexShrink: 0 }}>
                        Allergies:
                    </Typography>
                    <Typography variant="body2" sx={{ color: '#5C3A00', flex: 1, minWidth: 0, wordBreak: 'break-word' }}>
                        {allergies.join(' · ')}
                    </Typography>
                </Box>
            )}
        </Box>
    );
}
